import { z } from 'zod';

const forbiddenKeys = new Set(['__proto__', 'prototype', 'constructor']);
export const isSafeExtractionPath = (value: string): boolean =>
  value.length <= 200 &&
  value.split('.').every((key) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(key) && !forbiddenKeys.has(key));

export const webExtractionRuleSchema = z.object({
  field: z
    .string()
    .max(80)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/)
    .refine(isSafeExtractionPath),
  pattern: z.string().min(1).max(2048),
  flags: z
    .string()
    .regex(/^[imsu]*$/)
    .refine((s) => new Set(s).size === s.length)
    .default(''),
  group: z.number().int().min(0).max(99).default(0),
  mode: z.enum(['first', 'all']).default('first'),
  required: z.boolean().default(true),
  outputFormat: z.string().max(2048).optional(),
  resolveUrl: z.boolean().default(false),
});

export const webExtractionSchema = z.object({
  baseUrl: z.string().max(4096).optional(),
  headers: z
    .array(
      z.object({
        name: z.enum(['User-Agent', 'Accept', 'Accept-Language']),
        value: z
          .string()
          .max(512)
          .regex(/^[^\r\n]*$/),
      }),
    )
    .max(3)
    .default([]),
  timeoutMs: z.number().int().min(100).max(30000).default(15000),
  rules: z
    .array(webExtractionRuleSchema)
    .min(1)
    .max(10)
    .refine(
      (rules) => new Set(rules.map((rule) => rule.field)).size === rules.length,
      'Output field names must be unique',
    ),
});

export type WebExtractionRule = z.infer<typeof webExtractionRuleSchema>;
export type WebExtractionConfig = z.infer<typeof webExtractionSchema>;
